/**
 * Hook Error Management Utilities
 *
 * Standardized error state management for React hooks.
 * Provides consistent patterns for error handling, display, and recovery.
 */

import { useState, useCallback, useRef } from 'react';
import { isAndroid } from '../platform';
import {  } from 'react-native'
import Toast from 'react-native-root-toast';

import { handleApiError, getErrorMessage } from '../api/errorHandler';

// Android navigation bar offset - positions toast above nav buttons
// Toast.positions.BOTTOM is typically -20, use -80 on Android to clear nav buttons
const TOAST_BOTTOM_OFFSET = isAndroid() ? -80 : (Toast.positions?.BOTTOM ?? -20);

/**
 * Error severity levels
 */
export const ERROR_SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical',
};

/**
 * Toast styles by severity
 */
const TOAST_STYLES = {
  [ERROR_SEVERITY.INFO]: { backgroundColor: '#2196f3' },
  [ERROR_SEVERITY.WARNING]: { backgroundColor: '#ff9800' },
  [ERROR_SEVERITY.ERROR]: { backgroundColor: '#d32f2f' },
  [ERROR_SEVERITY.CRITICAL]: { backgroundColor: '#b71c1c' },
  success: { backgroundColor: '#259e23' },
};

/**
 * Show a styled toast notification
 * @param {string} message - Message to display
 * @param {string} type - Toast type: 'success', 'info', 'warning', 'error', 'critical'
 * @param {Object} options - Additional toast options
 */
export function showToast(message, type = 'error', options = {}) {
  const style = TOAST_STYLES[type] || TOAST_STYLES.error;

  Toast.show(message, {
    ...style,
    textColor: '#fff',
    duration: type === ERROR_SEVERITY.CRITICAL ? Toast.durations.LONG * 2 : Toast.durations.LONG,
    position: TOAST_BOTTOM_OFFSET,
    shadow: true,
    animation: true,
    ...options,
  });
}

/**
 * Show success toast
 * @param {string} message - Success message
 */
export function showSuccess(message) {
  showToast(message, 'success');
}

/**
 * Show error toast
 * @param {string} message - Error message
 */
export function showError(message) {
  showToast(message, ERROR_SEVERITY.ERROR);
}

/**
 * Create a standardized error object
 * @param {Error|string} error - The original error
 * @param {Object} options - Error options
 * @returns {Object} Standardized error object
 */
export function createError(error, options = {}) {
  const {
    context = null,
    severity = ERROR_SEVERITY.ERROR,
    recoverable = true,
    retryable = false,
  } = options;

  const message = typeof error === 'string' ? error : getErrorMessage(error);
  const originalError = typeof error === 'string' ? null : error;

  return {
    message,
    severity,
    context,
    recoverable,
    retryable,
    originalError,
    timestamp: Date.now(),
  };
}

/**
 * Hook for managing error state in a standardized way
 *
 * @param {Object} options - Hook configuration
 * @param {string} options.context - Error context for logging
 * @param {boolean} options.showToasts - Whether to show toast notifications (default: true)
 * @returns {Object} Error state and handlers
 *
 * @example
 * const { error, setError, clearError, handleError } = useErrorState({ context: 'Reports' });
 *
 * const fetchData = async () => {
 *   try {
 *     const data = await Util.get('Resty', '/items');
 *     return data;
 *   } catch (err) {
 *     handleError(err, { userMessage: 'Failed to load data' });
 *   }
 * };
 */
export function useErrorState(options = {}) {
  const { context = null, showToasts = true } = options;

  const [error, setErrorState] = useState(null);
  const lastErrorRef = useRef(null);

  /**
   * Clear the current error
   */
  const clearError = useCallback(() => {
    setErrorState(null);
    lastErrorRef.current = null;
  }, []);

  /**
   * Set an error directly
   * @param {Error|string|Object} err - Error to set
   * @param {Object} errorOptions - Error options
   */
  const setError = useCallback(
    (err, errorOptions = {}) => {
      if (!err) {
        clearError();
        return;
      }

      const errorObj = createError(err, { context, ...errorOptions });
      setErrorState(errorObj);
      lastErrorRef.current = errorObj;

      if (showToasts && errorOptions.silent !== true) {
        showToast(errorObj.message, errorObj.severity);
      }
    },
    [context, showToasts, clearError]
  );

  /**
   * Handle an API error with standardized behavior
   * @param {Error} err - The error to handle
   * @param {Object} errorOptions - Additional options
   */
  const handleError = useCallback(
    (err, errorOptions = {}) => {
      const { userMessage = null, silent = false, severity = ERROR_SEVERITY.ERROR } = errorOptions;

      // Use existing handleApiError for logging
      handleApiError(err, {
        context,
        silent: true, // We'll handle toast ourselves
        ...errorOptions,
      });

      // Set our standardized error state
      const errorObj = createError(err, {
        context,
        severity,
        ...errorOptions,
      });

      // Override message if custom provided
      if (userMessage) {
        errorObj.message = userMessage;
      }

      setErrorState(errorObj);
      lastErrorRef.current = errorObj;

      // Show toast unless silent
      if (showToasts && !silent) {
        showToast(errorObj.message, severity);
      }

      return errorObj;
    },
    [context, showToasts]
  );

  /**
   * Check if there's an active error
   */
  const hasError = error !== null;

  return {
    error,
    hasError,
    setError,
    clearError,
    handleError,
    lastError: lastErrorRef.current,
  };
}

/**
 * Hook for managing multiple field-level errors (form validation)
 *
 * @param {Object} options - Hook configuration
 * @returns {Object} Field errors state and handlers
 *
 * @example
 * const { errors, setFieldError, clearFieldError, hasErrors, validate } = useFieldErrors();
 *
 * const validateForm = () => {
 *   return validate({
 *     email: !email ? 'Email is required' : null,
 *     password: password.length < 8 ? 'Password too short' : null,
 *   });
 * };
 */
export function useFieldErrors() {
  const [errors, setErrors] = useState({});
  const [displayErrors, setDisplayErrors] = useState(false);

  /**
   * Set error for a specific field
   */
  const setFieldError = useCallback((field, message) => {
    setErrors((prev) => ({
      ...prev,
      [field]: message,
    }));
  }, []);

  /**
   * Clear error for a specific field
   */
  const clearFieldError = useCallback((field) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  /**
   * Clear all field errors
   */
  const clearAllErrors = useCallback(() => {
    setErrors({});
    setDisplayErrors(false);
  }, []);

  /**
   * Validate multiple fields at once
   * @param {Object} validations - Object with field names and error messages (null if valid)
   * @returns {boolean} True if all fields are valid
   */
  const validate = useCallback((validations) => {
    const newErrors = {};

    Object.entries(validations).forEach(([field, errorMessage]) => {
      if (errorMessage) {
        newErrors[field] = errorMessage;
      }
    });

    setErrors(newErrors);
    setDisplayErrors(true);

    return Object.keys(newErrors).length === 0;
  }, []);

  /**
   * Get error message for a field (only if displayErrors is true)
   */
  const getFieldError = useCallback(
    (field) => {
      return displayErrors ? errors[field] : null;
    },
    [errors, displayErrors]
  );

  /**
   * Check if form has any errors
   */
  const hasErrors = Object.keys(errors).length > 0;

  return {
    errors,
    displayErrors,
    setDisplayErrors,
    hasErrors,
    setFieldError,
    clearFieldError,
    clearAllErrors,
    validate,
    getFieldError,
  };
}

/**
 * Wrapper for async operations with standardized error handling
 *
 * @param {Function} asyncFn - Async function to wrap
 * @param {Object} options - Handler options
 * @returns {Function} Wrapped function that handles errors
 *
 * @example
 * const fetchData = withErrorHandling(
 *   async () => await Util.get('Resty', '/items'),
 *   { context: 'Reports', onError: setError }
 * );
 */
export function withErrorHandling(asyncFn, options = {}) {
  const { context = null, onError = null, silent = false, defaultValue = null } = options;

  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      handleApiError(error, { context, silent });

      if (onError) {
        onError(error);
      }

      return defaultValue;
    }
  };
}

/**
 * Retry an async operation with exponential backoff
 *
 * @param {Function} asyncFn - Async function to retry
 * @param {Object} options - Retry options
 * @returns {Promise} Result of the async function
 *
 * @example
 * const data = await withRetry(
 *   () => Util.get('Resty', '/items'),
 *   { maxRetries: 3, onRetry: (attempt) => console.log(`Retry ${attempt}`) }
 * );
 */
export async function withRetry(asyncFn, options = {}) {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    onRetry = null,
    shouldRetry = (error) => {
      // Retry on network errors or 5xx server errors
      const status = error.status || error.statusCode || error.response?.status;
      const isNetworkError = error.message?.includes('Network') || error.message?.includes('fetch');
      const isServerError = status >= 500 && status < 600;
      return isNetworkError || isServerError;
    },
  } = options;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await asyncFn();
    } catch (error) {
      lastError = error;

      if (attempt < maxRetries && shouldRetry(error)) {
        const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);

        if (onRetry) {
          onRetry(attempt + 1, error, delay);
        }

        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        break;
      }
    }
  }

  throw lastError;
}
